import uniqBy from 'lodash/uniqBy';
import { combineEpics, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { organizationTypeIdSel } from '@context/duck/selectors';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { setSelectedRosterKeys } from '@shared/duck/actions';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization/translate';
import toastService from '@toasts/service';
import { userIdSel } from '@user/duck/selectors';
import { catchAndReport } from '@utils/rxjs/operators';

import {
  loadAndOpenActivity,
  personsAndIdsActivitySel,
  personsToRemoveSel,
  savingAction,
  selectedActivitySel,
  setTime,
} from '../../common';
import {
  LOAD_AND_OPEN_CAMPOUT_MODAL,
  RECORD_CAMPOUTS_REQUEST,
  RECORD_CAMPOUTS_RESPONSE,
  closeCampouts,
  loadAndOpenCampoutModalError,
  openEditCampout,
  recordCampoutsError,
  recordCampoutsResponse,
} from './actions';
import { activeTabSel, personsSel } from './selectors';
import services from './services';

const recordCampoutsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(RECORD_CAMPOUTS_REQUEST),
    switchMap(({ payload }) => {
      const state = state$.value;
      const { activityData } = payload;
      let validActivityData = { ...activityData };

      if (activityData.allDay) {
        validActivityData = {
          ...validActivityData,
          startTime: setTime(),
          endTime: setTime(23, 45),
        };
      }

      const persons = personsSel(state);
      const personsToRemove = personsToRemoveSel(state);
      const loggedInUserId = userIdSel(state);
      const organizationGuid = organizationGuidSel(state);
      const organizationTypeId = organizationTypeIdSel(state);
      const selectedActivity = selectedActivitySel(state);
      const isAdvancedMode = activeTabSel(state) == 'advanced';
      const { personsInActivity } = personsAndIdsActivitySel(state);

      const uniquePersons = uniqBy(
        [...personsInActivity, ...persons],
        'userId',
      );

      const personsUserIds = uniquePersons?.map(person => person.userId) || [];
      const personsGuids =
        uniquePersons?.map(person => person.personGuid) || [];

      // Invalidate summary counts
      const tagsToInvalidate = personsUserIds.map(userId =>
        tagFactory.UserActivitySummary.userSummary(userId),
      );

      // Invalidate org activities
      tagsToInvalidate.push(
        tagFactory.Activities.orgActivities(organizationGuid),
      );

      // Invalidate youth activities
      tagsToInvalidate.push(
        tagFactory.Activities.youthActivities(selectedActivity?.personGuid),
        ...personsUserIds.map(userId =>
          tagFactory.Activities.youthActivities(userId),
        ),
        ...personsGuids.map(personGuid =>
          tagFactory.Activities.youthActivities(personGuid),
        ),
      );

      return services
        .saveCampoutActivity$({
          ...selectedActivity,
          ...validActivityData,
          personsToRemove,
          loggedInUserId,
          organizationGuid,
          organizationTypeId: +organizationTypeId,
          persons: uniquePersons,
          isAdvancedMode,
        })
        .pipe(
          mergeMap(() => {
            const { closeOnSuccess } = payload;
            const actions = [
              recordCampoutsResponse(),
              // Invalidate tags
              esbApi.util.invalidateTags(tagsToInvalidate),
            ].concat(
              closeOnSuccess
                ? [closeCampouts(), setSelectedRosterKeys([])]
                : [],
            );

            return of(...actions);
          }),
          tap(({ type }) => {
            if (type === RECORD_CAMPOUTS_RESPONSE) {
              toastService.success(
                intl.formatMessage(
                  {
                    id: `progress.common.${
                      persons.length ? 'saveSuccessParticipants' : 'saveSuccess'
                    }`,
                  },
                  {
                    name: activityData.name,
                    type: selectedActivity.id
                      ? savingAction.UPDATED
                      : savingAction.CREATED,
                    count: persons.length,
                    participants: persons
                      .map(({ personShortFullName }) => personShortFullName)
                      .join(', '),
                  },
                ),
              );
            }
          }),
          catchAndReport(err => of(recordCampoutsError(err))),
        );
    }),
  );

const loadCampoutByIdEpic$ = action$ =>
  action$.pipe(
    ofType(LOAD_AND_OPEN_CAMPOUT_MODAL),
    map(({ payload }) =>
      loadAndOpenActivity({
        ...payload,
        successActionFn: openEditCampout,
        errorActionFn: loadAndOpenCampoutModalError,
      }),
    ),
  );

export default combineEpics(recordCampoutsEpic$, loadCampoutByIdEpic$);
